import type { Game } from '../core/game';
import { BALANCE } from '../data/balance';
import { NODE_BY_ID } from '../data/nodes';
import { formatCount } from '../battle/format';
import { formatNumber } from './format';
import { hasMethod, nodeLevel } from './gameApi';
import type { IconName } from './icons';
import type { MaterialFacts } from './materialsApi';
import { nodeIconName } from './treeGraph';

/**
 * Indicateurs de talents et compteur d'ennemis du HUD de combat (A88-4, A88-6).
 *
 * Deux parties :
 *  - des fonctions **pures** de formatage et de décision (testées sans DOM) ;
 *  - des **lecteurs dynamiques** du cœur, qui ne supposent rien : le lot cœur
 *    (`state.combat.kills`, `critStreak`, `spellStreak`, `secondsGained`,
 *    `game.critMachineRemaining()`, `critMachineFactor()`, `streakMultipliers()`,
 *    `expeditionEnemyCap()`) peut manquer, le HUD retombe alors sur une valeur
 *    neutre ou masque l'indicateur — jamais d'erreur.
 *
 * v0.47.0 (A95-4) : les indicateurs descendent en bas du cadre sous forme de
 * **puces** (icône du nœud de l'arbre + libellé court), les Matériaux y gagnent
 * les leurs. `bottomChips` décide, sans DOM, quelles puces existent, leur texte
 * et leur état `on` / `off` ; l'écran (`combatHud.ts`) ne fait que les poser.
 *
 * v0.49.0 (A98) : la puce `enemy-effect` disparaît (les effets de l'ennemi vivent
 * dans sa bannière, `game3d/overlay.ts`) ; quatre puces s'ajoutent — **Tempête**
 * (`stormRemaining()`), **Marque Null** (`enemy.nullMark.held`), **Armure**
 * (`enemy.armorStacks`) et **42** (`state.combat.fortyTwoCopies`) — lues
 * dynamiquement (le cœur A98 peut manquer : repli neutre, la puce se tait).
 * « +x s » ne compte plus 42 : seul *Temporel* donne des secondes.
 *
 * v0.50.0 (A100-2) : la puce *Préparation* disparaît avec le compteur du cœur ;
 * le talent est devenu un renfort des sorts de dé des emplacements 1 à 3, qui se
 * lit dans le récapitulatif des dégâts (`damageRecap.ts`).
 */

/** Nœuds des talents indiqués (les mêmes identifiants que `src/data/nodes.ts`). */
export const CRIT_MACHINE_NODE = 'ars_critmachine';
export const CRIT_STREAK_NODE = 'ars_critstreak';
export const SPELL_STREAK_NODE = 'arc_flux';

// ─────────────────────────────────────────────────────────── Formatage pur

/** Compteur d'ennemis : « 37 / 50 ». */
export function formatEnemyCount(done: number, total: number): string {
  return `${Math.max(0, Math.floor(done))} / ${Math.max(0, Math.floor(total))}`;
}

/** Secondes gagnées par *Temporel* (42 n'en donne plus, A98) : « +4,5 s », « +0 s ». */
export function formatSecondsGained(seconds: number): string {
  const s = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  return `+${formatNumber(s, 1)} s`;
}

/** Multiplicateur d'indicateur : « ×1,13 » (deux décimales, zéros de fin retirés). */
export function formatFactor(n: number): string {
  return `×${formatNumber(n, 2)}`;
}

export interface StreakView {
  /** Texte de la puce : « Critiques ×7 · ×1,13 », ou « Critiques ×1 » au repos. */
  text: string;
  /** Vrai dès deux coups consécutifs ; sinon la puce est grisée. */
  active: boolean;
}

/** Puce d'une série (Criticité : « Critiques », Flux arcanique : « Sorts »). */
export function streakView(label: string, streak: number, multiplier: number): StreakView {
  const n = Number.isFinite(streak) ? Math.max(0, Math.floor(streak)) : 0;
  if (n <= 1) return { text: `${label} ×1`, active: false };
  return { text: `${label} ×${n} · ${formatFactor(multiplier)}`, active: true };
}

export interface MachineView {
  active: boolean;
  /** Remplissage de la jauge, de 0 (vide) à 1 (pleine). */
  fill: number;
  /** « Machine critique ×0,75 » quand active, « Machine critique » sinon. */
  text: string;
}

/** Puce de *Machine Critique* : la jauge se vide sur `duration` secondes. */
export function machineView(remaining: number, factor: number, duration: number): MachineView {
  const r = Number.isFinite(remaining) ? Math.max(0, remaining) : 0;
  const d = duration > 0 ? duration : 1;
  const active = r > 0;
  return {
    active,
    fill: active ? Math.min(1, r / d) : 0,
    text: active ? `Machine critique ${formatFactor(factor)}` : 'Machine critique',
  };
}

// ──────────────────────────────────────────────────── Puces du bas (A95-4)

export type ChipState = 'on' | 'off';

/** Une puce de la rangée du bas, prête à être posée. */
export interface ChipView {
  /** Identifiant stable (`data-chip`). */
  chip: string;
  /** Libellé court, à côté de l'icône (« ×1,13 », « 4,2 s »). */
  label: string;
  /** Texte complet : `aria-label` et `title` (« Critiques ×7 · ×1,13 »). */
  full: string;
  state: ChipState;
  icon: IconName;
  /** Matériau de la puce (teinte, `data-material`). */
  material?: string;
  /** Jauge sous la puce, de 0 à 1 (Machine Critique). */
  fill?: number;
}

/** Ordre d'apparition des puces dans la rangée (les absentes sont masquées). */
export const CHIP_ORDER = [
  'stun',
  'critmachine',
  'critstreak',
  'spellstreak',
  'seconds',
  'storm',
  'nullmark',
  'armor',
  'fortytwo',
  'wood',
  'wood5',
  'bone',
  'iron',
  'rubber',
  'silicon',
  'silicon5',
  'void',
  'void5',
  // v0.58.0 (A123) : dés Prismatiques nommés à effet de combat.
  'wingedrat',
  'andariel',
] as const;

export type ChipId = (typeof CHIP_ORDER)[number];

/** Icône de la puce d'un talent : **la même règle que l'arbre** (`nodeIconName`). */
export function chipIconName(nodeId: string): IconName {
  const def = NODE_BY_ID[nodeId];
  if (!def) return 'node';
  return nodeIconName({ name: def.name, branch: def.branch, description: def.description });
}

/**
 * Icône des puces qui n'ont pas de nœud : secondes gagnées, Assommé, les sorts
 * de dé (Tempête, Null, 42), l'Armure de l'ennemi et les Matériaux.
 */
const FREE_ICON: Record<'seconds' | 'stun' | 'storm' | 'nullmark' | 'armor' | 'fortytwo' | 'material', IconName> = {
  seconds: 'clock',
  stun: 'lock',
  storm: 'spell',
  nullmark: 'target',
  armor: 'shield',
  fortytwo: 'die',
  material: 'abyss',
};

/** Icône d'une puce, fixe pour un identifiant donné (le HUD crée le DOM une fois). */
export function chipIconOf(id: ChipId): IconName {
  switch (id) {
    case 'storm':
    case 'nullmark':
    case 'armor':
    case 'fortytwo':
      return FREE_ICON[id];
    case 'stun':
      return FREE_ICON.stun;
    case 'critmachine':
      return chipIconName(CRIT_MACHINE_NODE);
    case 'critstreak':
      return chipIconName(CRIT_STREAK_NODE);
    case 'spellstreak':
      return chipIconName(SPELL_STREAK_NODE);
    case 'seconds':
      return FREE_ICON.seconds;
    case 'wingedrat':
      return 'affix';
    case 'andariel':
      return 'die';
    default:
      return FREE_ICON.material;
  }
}

/** Libellé court d'une série : « ×1,13 » une fois active, « ×1 » au repos. */
export function streakShort(streak: number, multiplier: number): string {
  const n = Number.isFinite(streak) ? Math.max(0, Math.floor(streak)) : 0;
  return n <= 1 ? '×1' : formatFactor(multiplier);
}

/** Libellé court des secondes gagnées : « 4,2 s » (le « + » reste dans le texte complet). */
export function secondsShort(seconds: number): string {
  const s = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  return `${formatNumber(s, 1)} s`;
}

/** Ce que `bottomChips` lit : tout est déjà résolu (aucun accès au cœur ici). */
export interface BottomFacts {
  stunned: boolean;
  machine: { owned: boolean; remaining: number; factor: number; duration: number };
  critStreak: { owned: boolean; streak: number; mult: number };
  spellStreak: { owned: boolean; streak: number; mult: number };
  /** `visible` : un dé *Temporel* est équipé et le cœur publie le compteur. */
  seconds: { visible: boolean; gained: number };
  /**
   * *Tempête* (A98) : `visible` quand un dé équipé porte le sort ou qu'une tempête
   * souffle ; `remaining` en secondes, `reduction` du temps de retour (0,25).
   */
  storm: { visible: boolean; remaining: number; reduction: number };
  /** Marque de *Null* (A98, A100) : `held` = dégâts accumulés dans la marque, `null` sans marque posée. */
  nullMark: { visible: boolean; held: number | null };
  /** Armure de l'ennemi (A98) : coups cumulés et facteur par coup (1,05). */
  armor: { visible: boolean; stacks: number; perHit: number };
  /** Sort *42* (A98) : dés qui seront lancés une fois de plus au prochain lancer. */
  fortyTwo: { visible: boolean; copies: number };
  materials: MaterialFacts;
  /**
   * Dés nommés de l'A123 (facultatif : absent, aucune puce). *Rat-Ailé* :
   * `visible` s'il est équipé, `active` sans bonus de Matériau actif, `factor`
   * (1,2). *Andarielle* : `visible` s'il est équipé, `doubled` quand le poison
   * de l'ennemi courant a été doublé.
   */
  named?: {
    wingedRat: { visible: boolean; active: boolean; factor: number };
    andariel: { visible: boolean; doubled: boolean };
  };
}

/** Bonus actif d'un Matériau à un palier (3 ou 5 pièces) ? */
function hasTier(m: MaterialFacts, material: string, tier: 3 | 5): boolean {
  return m.bonuses.some((b) => b.material === material && b.tier === tier);
}

/**
 * Puces des Matériaux (A95-4) : une par bonus actif, dans l'ordre Boisé,
 * Osseux, Ferreux, Caoutchouc, Silicium, Du Vide. Allumée (`on`) quand le
 * bonus a quelque chose à dire — Boisé une fois le +10 s utilisé, Osseux sur un
 * ennemi brisé, Silicium avec un excédent en attente, Du Vide sur un ennemi
 * dont les effets sont annulés — grisée (`off`) sinon.
 */
export function materialChips(m: MaterialFacts): ChipView[] {
  const { runtime: r, constants: c } = m;
  const icon = FREE_ICON.material;
  const out: ChipView[] = [];

  if (hasTier(m, 'wood', 3)) {
    out.push({
      chip: 'wood',
      label: `+${formatNumber(c.woodSeconds, 0)} s`,
      full: `Boisé : +${formatNumber(c.woodSeconds, 0)} s une fois par Invasion (${r.woodUsed ? 'utilisé' : 'disponible'})`,
      state: r.woodUsed ? 'on' : 'off',
      icon,
      material: 'wood',
    });
  }
  if (hasTier(m, 'wood', 5)) {
    out.push({
      chip: 'wood5',
      label: 'Contenant : max',
      full: 'Boisé : le contenant inflige le maximum entre le lancer précédent et celui-ci',
      state: r.lastBasicSum > 0 ? 'on' : 'off',
      icon,
      material: 'wood',
    });
  }

  if (hasTier(m, 'bone', 3) || hasTier(m, 'bone', 5)) {
    const mult = boneMultiplier(r.broken, c.boneBase, c.boneStep);
    out.push({
      chip: 'bone',
      label: `Brisé ${formatFactor(mult)}`,
      full:
        r.broken > 0
          ? `Osseux : ennemi brisé ${r.broken > 1 ? `${r.broken} fois ` : ''}— dégâts ${formatFactor(mult)}`
          : `Osseux : un sort d'emplacement brise l'ennemi (dégâts ${formatFactor(mult)})`,
      state: r.broken > 0 ? 'on' : 'off',
      icon,
      material: 'bone',
    });
  }

  if (hasTier(m, 'iron', 3) || hasTier(m, 'iron', 5)) {
    const base = hasTier(m, 'iron', 3) ? c.ironBase : 1;
    // Ferreux 5 : le facteur du dernier lancer (il compte le ×1,25 de Ferreux 3
    // quand les deux sont actifs) ; sans lancer, le facteur de base.
    const mult = hasTier(m, 'iron', 5) && r.ironMult > 1 ? r.ironMult : base;
    out.push({
      chip: 'iron',
      label: mult > 1 ? `Basiques ${formatFactor(mult)}` : 'Basiques ×2 / max',
      full:
        mult > 1
          ? `Ferreux : dés Basiques ${formatFactor(mult)}`
          : 'Ferreux : dés Basiques ×2 par face maximale des dés Maîtres',
      state: mult > 1 ? 'on' : 'off',
      icon,
      material: 'iron',
    });
  }

  if (hasTier(m, 'rubber', 3) || hasTier(m, 'rubber', 5)) {
    const wide = hasTier(m, 'rubber', 5);
    out.push({
      chip: 'rubber',
      label: wide ? `Rebond ${c.rubberSmall}-${c.rubberLarge}` : `Rebond ${c.rubberSmall}`,
      full: wide
        ? `Caoutchouc : les dés des emplacements ${c.rubberSmall} à ${c.rubberLarge} rebondissent`
        : `Caoutchouc : le dé de l'emplacement ${c.rubberSmall} rebondit`,
      state: 'on',
      icon,
      material: 'rubber',
    });
  }

  if (hasTier(m, 'silicon', 3)) {
    out.push({
      chip: 'silicon',
      label: `Report +${formatCount(r.overkillCarry)}`,
      full:
        r.overkillCarry > 0
          ? `Silicium : ${formatCount(r.overkillCarry)} dégâts reportés sur le prochain lancer`
          : 'Silicium : l’excédent d’un lancer qui tue s’ajoute au suivant',
      state: r.overkillCarry > 0 ? 'on' : 'off',
      icon,
      material: 'silicon',
    });
  }
  if (hasTier(m, 'silicon', 5)) {
    out.push({
      chip: 'silicon5',
      label: `Électrification : ${r.electrifyStacks} cumul${r.electrifyStacks > 1 ? 's' : ''} (+${formatNumber(Math.round(r.electrifyStacks * c.electrifyStep * 10000) / 100, 2)} %)`,
      full: 'Silicium : chaque lancer ajoute un cumul d’Électrification (+1 % de dégâts par cumul), remis à zéro à chaque combat',
      state: r.electrifyStacks > 0 ? 'on' : 'off',
      icon,
      material: 'silicon',
    });
  }

  if (hasTier(m, 'void', 3)) {
    out.push({
      chip: 'void',
      label: 'Effets annulés',
      full: r.effectVoided ? 'Du Vide : les effets de ce monstre sont annulés' : 'Du Vide : annule les effets du monstre',
      state: r.effectVoided ? 'on' : 'off',
      icon,
      material: 'void',
    });
  }
  if (hasTier(m, 'void', 5)) {
    out.push({
      chip: 'void5',
      label: 'Lancer : max',
      full: 'Du Vide : le lancer inflige le maximum entre le lancer précédent et celui-ci',
      state: r.lastTotal > 0 ? 'on' : 'off',
      icon,
      material: 'void',
    });
  }
  return out;
}

/** Multiplicateur d'un ennemi brisé `n` fois : 1,5 puis +0,5 par bris supplémentaire (×1,5, ×2, ×2,5…). */
export function boneMultiplier(broken: number, base = 1.5, step = 0.5): number {
  const n = Number.isFinite(broken) ? Math.max(1, Math.floor(broken)) : 1;
  return base + step * (n - 1);
}

/**
 * Toutes les puces à montrer, dans l'ordre de `CHIP_ORDER`. Une puce de talent
 * n'existe que si le nœud est possédé (comme les anciennes puces d'A88-6) ;
 * Assommé, que s'il est en cours ; les puces de sorts et d'armure (A98), que si
 * le sort est porté ou l'effet présent — grisées tant qu'elles n'ont rien à dire.
 */
export function bottomChips(f: BottomFacts): ChipView[] {
  const out: ChipView[] = [];

  if (f.stunned) {
    out.push({ chip: 'stun', label: 'Assommé', full: 'Assommé : aucun lancer possible', state: 'on', icon: FREE_ICON.stun });
  }
  if (f.machine.owned) {
    const mv = machineView(f.machine.remaining, f.machine.factor, f.machine.duration);
    out.push({
      chip: 'critmachine',
      label: mv.active ? formatFactor(f.machine.factor) : '×1',
      full: mv.text,
      state: mv.active ? 'on' : 'off',
      icon: chipIconName(CRIT_MACHINE_NODE),
      fill: mv.fill,
    });
  }
  if (f.critStreak.owned) {
    const v = streakView('Critiques', f.critStreak.streak, f.critStreak.mult);
    out.push({
      chip: 'critstreak',
      label: streakShort(f.critStreak.streak, f.critStreak.mult),
      full: v.text,
      state: v.active ? 'on' : 'off',
      icon: chipIconName(CRIT_STREAK_NODE),
    });
  }
  if (f.spellStreak.owned) {
    const v = streakView('Sorts', f.spellStreak.streak, f.spellStreak.mult);
    out.push({
      chip: 'spellstreak',
      label: streakShort(f.spellStreak.streak, f.spellStreak.mult),
      full: v.text,
      state: v.active ? 'on' : 'off',
      icon: chipIconName(SPELL_STREAK_NODE),
    });
  }
  if (f.seconds.visible) {
    out.push({
      chip: 'seconds',
      label: secondsShort(f.seconds.gained),
      // A98 : 42 ne donne plus de secondes, seul Temporel en gagne.
      full: `Secondes gagnées par Temporel ${formatSecondsGained(f.seconds.gained)}`,
      state: f.seconds.gained > 0 ? 'on' : 'off',
      icon: FREE_ICON.seconds,
    });
  }
  out.push(...spellChips(f));
  out.push(...materialChips(f.materials));
  out.push(...namedChips(f));
  return out;
}

/**
 * Puces des dés nommés de l'A123 : *Rat-Ailé* « Rat-Ailé : affixes ×1,2 »,
 * allumée tant qu'aucun bonus de Matériau n'est actif (éteinte sinon) ;
 * *Andarielle* « Poison doublé », allumée une fois le poison de l'ennemi
 * courant doublé.
 */
export function namedChips(f: BottomFacts): ChipView[] {
  const out: ChipView[] = [];
  const rat = f.named?.wingedRat;
  if (rat?.visible) {
    const label = `Rat-Ailé : affixes ${formatFactor(rat.factor)}`;
    out.push({
      chip: 'wingedrat',
      label,
      full: rat.active
        ? `${label} (aucun bonus de Matériau actif)`
        : `${label} — éteint : un bonus de Matériau est actif`,
      state: rat.active ? 'on' : 'off',
      icon: 'affix',
    });
  }
  const and = f.named?.andariel;
  if (and?.visible) {
    out.push({
      chip: 'andariel',
      label: and.doubled ? 'Andarielle : poison doublé' : 'Andarielle',
      full: and.doubled
        ? 'Andarielle : le poison de ce monstre a été doublé'
        : 'Andarielle : le poison sera doublé dès qu’il excédera les PV restants du monstre (une fois par monstre)',
      state: and.doubled ? 'on' : 'off',
      icon: 'die',
    });
  }
  return out;
}

/**
 * Dés nommés de l'A123 équipés et leur état (lecture dynamique, neutre sans le
 * cœur A123) : *Rat-Ailé* par `game.wingedRatState()`, *Andarielle* par
 * l'affixe d'un dé équipé et `enemy.poisonDoubled`.
 */
export function readNamedFacts(game: Game): NonNullable<BottomFacts['named']> {
  let rat = { visible: false, active: false, factor: BALANCE.wingedRatAffixFactor };
  if (hasMethod(game, 'wingedRatState')) {
    try {
      const st = (game as unknown as { wingedRatState(): { equipped?: unknown; active?: unknown } }).wingedRatState();
      rat = { ...rat, visible: st?.equipped === true, active: st?.active === true };
    } catch {
      /* neutre */
    }
  }
  const st = game.state;
  let andariel = false;
  for (const id of st.equipped) {
    if (!id) continue;
    const die = st.inventory.find((d) => d.id === id);
    if ((die?.affixes ?? []).some((a) => (a.id as string) === 'andariel')) andariel = true;
  }
  const doubled = (enemyRecord(game)?.poisonDoubled ?? false) === true;
  return { wingedRat: rat, andariel: { visible: andariel, doubled } };
}

/** « −25 % » : réduction du temps de retour de *Tempête*, en pourcentage entier. */
export function stormPercent(reduction: number): string {
  const r = Number.isFinite(reduction) ? Math.min(1, Math.max(0, reduction)) : 0;
  return `−${Math.round(r * 100)} %`;
}

/** « ×0,95 » : facteur d'*Armure* après `stacks` coups (`perHit` ^ −`stacks`, perHit = 1,05). */
export function armorFactor(stacks: number, perHit: number): number {
  const k = Number.isFinite(stacks) ? Math.max(0, Math.floor(stacks)) : 0;
  const p = Number.isFinite(perHit) && perHit > 1 ? perHit : 1.05;
  return 1 / Math.pow(p, k);
}

/**
 * Puces des sorts de dé et de l'armure (A98) : Tempête, Marque Null, Armure, 42.
 * Chacune n'existe que si elle peut servir (`visible`) et se grise tant que sa
 * valeur est nulle (« −25 % · 4,2 s » allumée, « Tempête » éteinte).
 */
export function spellChips(f: BottomFacts): ChipView[] {
  const out: ChipView[] = [];
  if (f.storm.visible) {
    const on = f.storm.remaining > 0;
    const pct = stormPercent(f.storm.reduction);
    out.push({
      chip: 'storm',
      label: on ? `${pct} · ${secondsShort(f.storm.remaining)}` : 'Tempête',
      full: on
        ? `Tempête : temps de retour ${pct} pendant encore ${secondsShort(f.storm.remaining)}`
        : `Tempête : temps de retour ${pct} pendant quelques secondes après le sort`,
      state: on ? 'on' : 'off',
      icon: FREE_ICON.storm,
    });
  }
  if (f.nullMark.visible) {
    const held = f.nullMark.held;
    const on = held !== null;
    out.push({
      chip: 'nullmark',
      label: on ? `Marque : ${formatCount(held)}` : 'Marque Null',
      full: on
        ? `Marque Null : ${formatCount(held)} dégâts accumulés, libérés au prochain déclenchement de Null`
        : 'Marque Null : un premier déclenchement pose la marque, les lancers suivants frappent et s’y accumulent',
      state: on ? 'on' : 'off',
      icon: FREE_ICON.nullmark,
    });
  }
  if (f.armor.visible) {
    const stacks = Math.max(0, Math.floor(f.armor.stacks));
    const factor = armorFactor(stacks, f.armor.perHit);
    out.push({
      chip: 'armor',
      label: formatFactor(factor),
      full:
        stacks > 0
          ? `Armure : ${stacks} coup${stacks > 1 ? 's' : ''} reçu${stacks > 1 ? 's' : ''}, dégâts du lancer ${formatFactor(factor)}`
          : 'Armure : les dégâts du lancer baissent à chaque coup reçu (×1)',
      state: stacks > 0 ? 'on' : 'off',
      icon: FREE_ICON.armor,
    });
  }
  if (f.fortyTwo.visible) {
    const n = Math.max(0, Math.floor(f.fortyTwo.copies));
    out.push({
      chip: 'fortytwo',
      label: n > 0 ? `${n} ${n > 1 ? 'copies' : 'copie'} au prochain lancer` : '42',
      full:
        n > 0
          ? `42 : ${n} dé${n > 1 ? 's' : ''} lancé${n > 1 ? 's' : ''} une fois de plus au prochain lancer`
          : '42 : les dés qui montrent 4 et 2 sont relancés une fois au lancer suivant',
      state: n > 0 ? 'on' : 'off',
      icon: FREE_ICON.fortytwo,
    });
  }
  return out;
}

// ───────────────────────────────────────────────────── Lecteurs dynamiques

function combatRecord(game: Game): Record<string, unknown> {
  return ((game.state as unknown as { combat?: Record<string, unknown> }).combat ?? {}) as Record<string, unknown>;
}

function finite(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function callNumber(game: Game, name: string): number | null {
  if (!hasMethod(game, name)) return null;
  try {
    return finite((game as unknown as Record<string, () => unknown>)[name].call(game));
  } catch {
    return null;
  }
}

/** Le talent est-il possédé ? (relu à chaque rendu : un achat de debug se voit) */
export function owns(game: Game, nodeId: string): boolean {
  return nodeLevel(game, nodeId) >= 1;
}

/** État de *Machine Critique* ; `null` si le cœur ne sait pas encore le dire. */
export function readCritMachine(game: Game): { remaining: number; factor: number } | null {
  const remaining = callNumber(game, 'critMachineRemaining');
  if (remaining === null) return null;
  return { remaining, factor: callNumber(game, 'critMachineFactor') ?? 1 };
}

/** Durée de la fenêtre de *Machine Critique*, en secondes (données d'équilibrage). */
export function critMachineDuration(): number {
  return BALANCE.critMachineDuration;
}

export interface StreakFacts {
  critStreak: number;
  spellStreak: number;
  critMult: number;
  spellMult: number;
}

/** Séries consécutives et leurs multiplicateurs (neutres tant que le cœur manque). */
export function readStreaks(game: Game): StreakFacts {
  const combat = combatRecord(game);
  const top = game.state as unknown as Record<string, unknown>;
  let critMult = 1;
  let spellMult = 1;
  if (hasMethod(game, 'streakMultipliers')) {
    try {
      const m = (game as unknown as { streakMultipliers(): { crit?: unknown; spell?: unknown } }).streakMultipliers();
      critMult = finite(m?.crit) ?? 1;
      spellMult = finite(m?.spell) ?? 1;
    } catch {
      /* neutre */
    }
  }
  return {
    // Repli sur les séries de la run (`state.critStreak`) tant que le cœur ne
    // les publie pas aussi dans `state.combat`.
    critStreak: finite(combat.critStreak) ?? finite(top.critStreak) ?? 0,
    spellStreak: finite(combat.spellStreak) ?? finite(top.spellStreak) ?? 0,
    critMult,
    spellMult,
  };
}

/** Un dé équipé porte-t-il l'un de ces sorts ? (identifiants des données, relus à chaque rendu) */
export function carriesSpell(game: Game, ids: readonly string[]): boolean {
  const st = game.state;
  for (const id of st.equipped) {
    if (!id) continue;
    const die = st.inventory.find((d) => d.id === id);
    if (!die) continue;
    if (((die.spells ?? []) as string[]).some((s) => ids.includes(s))) return true;
  }
  return false;
}

/**
 * Un dé *Temporel* est-il équipé ? C'est la seule source de secondes gagnées
 * (A98 : *42* n'en donne plus ; *Boisé* a sa propre puce).
 */
export function carriesSecondsSource(game: Game): boolean {
  const st = game.state;
  for (const id of st.equipped) {
    if (!id) continue;
    const die = st.inventory.find((d) => d.id === id);
    if (!die) continue;
    if ((die.affixes ?? []).some((a) => (a.id as string) === 'temporal')) return true;
  }
  return false;
}

/** Secondes gagnées dans le combat (`state.combat.secondsGained`) ; `null` si absent. */
export function readSecondsGained(game: Game): number | null {
  return finite(combatRecord(game).secondsGained);
}

/**
 * Compteur d'ennemis : Expédition « tués / plafond » (plafond du cœur, absent
 * ou nul → pas de compteur), Invasion « enemyIndex / enemiesTotal ».
 */
export function readEnemyCount(game: Game): { done: number; total: number } | null {
  const s = game.state;
  if (s.phase === 'invasion') {
    return s.enemiesTotal > 0 ? { done: s.enemyIndex, total: s.enemiesTotal } : null;
  }
  if (s.phase !== 'expedition') return null;
  const cap = callNumber(game, 'expeditionEnemyCap');
  const kills = finite(combatRecord(game).kills);
  if (cap === null || cap <= 0 || kills === null) return null;
  return { done: kills, total: cap };
}

// ─────────────────────────────── Tempête, Null, Armure, 42 (A98, lecture dynamique)

/** Identifiants des sorts de dé de l'A98 (`data/dieSpells.ts`) et de 42, tels que les dés les portent. */
export const STORM_SPELL_IDS = ['storm'] as const;
export const NULL_SPELL_IDS = ['nullMark'] as const;
export const FORTY_TWO_SPELL_IDS = ['fortyTwo', '42'] as const;

function enemyRecord(game: Game): Record<string, unknown> | null {
  const e = (game.state as unknown as { enemy?: unknown }).enemy;
  return typeof e === 'object' && e !== null ? (e as Record<string, unknown>) : null;
}

/** Réduction du temps de retour de *Tempête* quand le cœur ne dit rien (COMMENTS v0.48.1 §1.3.2 : −25 %). */
export const STORM_REDUCTION_FALLBACK = 0.25;

/** Tempête : secondes restantes (`Game.stormRemaining()`, sinon `state.combat.stormRemaining`) ; `null` si absent. */
export function readStormRemaining(game: Game): number | null {
  return callNumber(game, 'stormRemaining') ?? finite(combatRecord(game).stormRemaining);
}

/** Réduction du temps de retour par la tempête en cours : `1 − stormFactor()`, repli −25 %. */
export function readStormReduction(game: Game): number {
  const factor = callNumber(game, 'stormFactor');
  if (factor !== null && factor > 0 && factor < 1) return 1 - factor;
  return STORM_REDUCTION_FALLBACK;
}

/** Marque de *Null* sur l'ennemi : dégâts accumulés, ou `null` sans marque (ou sans cœur A98). */
export function readNullHeld(game: Game): number | null {
  const mark = enemyRecord(game)?.nullMark;
  if (typeof mark !== 'object' || mark === null) return null;
  return finite((mark as { held?: unknown }).held) ?? 0;
}

/** Armure de l'ennemi : coups cumulés et facteur par coup ; `present` dit si l'ennemi en porte. */
export function readArmor(game: Game): { present: boolean; stacks: number; perHit: number } {
  const enemy = enemyRecord(game);
  const stacks = Math.max(0, Math.floor(finite(enemy?.armorStacks) ?? 0));
  let perHit = 1.05;
  let present = false;
  const effects = Array.isArray(enemy?.effects) ? (enemy?.effects as unknown[]) : enemy?.effect ? [enemy.effect] : [];
  for (const raw of effects) {
    const e = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
    if (e.kind !== 'armor') continue;
    present = true;
    perHit = finite(e.perHit) ?? perHit;
  }
  return { present: present || stacks > 0, stacks, perHit };
}

/** Copies de 42 en attente (`state.combat.fortyTwoCopies`) ; `null` tant que le cœur A98 ne les publie pas. */
export function readFortyTwoCopies(game: Game): number | null {
  const list = combatRecord(game).fortyTwoCopies;
  return Array.isArray(list) ? list.length : null;
}
